import React, { useRef, useCallback, useEffect } from 'react';
import { Button, ButtonGroup, Dropdown } from 'react-bootstrap';
import { 
    FaBold, 
    FaItalic, 
    FaUnderline, 
    FaStrikethrough,
    FaListUl,
    FaListOl,
    FaAlignLeft,
    FaAlignCenter,
    FaAlignRight,
    FaFont,
    FaPalette,
    FaUndo,
    FaRedo
} from 'react-icons/fa';
import './CustomRichTextEditor.css';

const CustomRichTextEditor = ({ value, onChange, placeholder, style, alignmentOnly = false }) => {
    const editorRef = useRef(null);
    const savedSelectionRef = useRef(null);

    // Initialize editor content
    useEffect(() => {
        if (editorRef.current && value !== editorRef.current.innerHTML) {
            editorRef.current.innerHTML = value || '';
        }
    }, [value]);

    // Save selection when editor loses focus
    const saveSelection = useCallback(() => {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            savedSelectionRef.current = selection.getRangeAt(0);
        }
    }, []);

    // Restore selection
    const restoreSelection = useCallback(() => {
        if (savedSelectionRef.current) {
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(savedSelectionRef.current);
        }
    }, []);

    // Handle content changes
    const handleInput = useCallback(() => {
        if (editorRef.current && onChange) {
            const content = editorRef.current.innerHTML;
            onChange(content);
        }
    }, [onChange]);

    // Execute formatting commands
    const execCommand = useCallback((command, value = null) => {
        restoreSelection();
        document.execCommand(command, false, value);
        editorRef.current?.focus();
        handleInput();
        saveSelection();
    }, [handleInput, restoreSelection, saveSelection]);

    // Formatting functions
    const formatBold = () => execCommand('bold');
    const formatItalic = () => execCommand('italic');
    const formatUnderline = () => execCommand('underline');
    const formatStrikethrough = () => execCommand('strikethrough');
    const formatUnorderedList = () => execCommand('insertUnorderedList');
    const formatOrderedList = () => execCommand('insertOrderedList');
    const formatAlignLeft = () => execCommand('justifyLeft');
    const formatAlignCenter = () => execCommand('justifyCenter');
    const formatAlignRight = () => execCommand('justifyRight');
    const formatUndo = () => execCommand('undo');
    const formatRedo = () => execCommand('redo');

    // Font size options
    const fontSizes = [
        { label: 'Small', value: '1' },
        { label: 'Normal', value: '3' },
        { label: 'Large', value: '5' },
        { label: 'Extra Large', value: '7' }
    ];

    // Color options
    const colors = [
        '#000000', '#FF0000', '#00FF00', '#0000FF',
        '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500',
        '#800080', '#008000', '#800000', '#000080'
    ];

    const handleFontSize = (size) => {
        execCommand('fontSize', size);
    };

    const handleTextColor = (color) => {
        execCommand('foreColor', color);
    };

    const handleBackgroundColor = (color) => {
        execCommand('backColor', color);
    };

    return (
        <div className="custom-rich-text-editor" style={style}>
            {/* Toolbar */}
            <div className="editor-toolbar">
                {alignmentOnly ? (
                    // Alignment-only toolbar preset
                    <ButtonGroup size="sm" className="me-2">
                        <Button type="button" variant="outline-secondary" onClick={formatAlignLeft} title="Align Left">
                            <FaAlignLeft />
                        </Button>
                        <Button type="button" variant="outline-secondary" onClick={formatAlignCenter} title="Align Center">
                            <FaAlignCenter />
                        </Button>
                        <Button type="button" variant="outline-secondary" onClick={formatAlignRight} title="Align Right">
                            <FaAlignRight />
                        </Button>
                    </ButtonGroup>
                ) : (
                    <>
                        <ButtonGroup size="sm" className="me-2">
                            <Button type="button" variant="outline-secondary" onClick={formatBold} title="Bold">
                                <FaBold />
                            </Button>
                            <Button type="button" variant="outline-secondary" onClick={formatItalic} title="Italic">
                                <FaItalic />
                            </Button>
                            <Button type="button" variant="outline-secondary" onClick={formatUnderline} title="Underline">
                                <FaUnderline />
                            </Button>
                            <Button type="button" variant="outline-secondary" onClick={formatStrikethrough} title="Strikethrough">
                                <FaStrikethrough />
                            </Button>
                        </ButtonGroup>

                        <ButtonGroup size="sm" className="me-2">
                            <Button type="button" variant="outline-secondary" onClick={formatUnorderedList} title="Bullet List">
                                <FaListUl />
                            </Button>
                            <Button type="button" variant="outline-secondary" onClick={formatOrderedList} title="Numbered List">
                                <FaListOl />
                            </Button>
                        </ButtonGroup>

                        <ButtonGroup size="sm" className="me-2">
                            <Button type="button" variant="outline-secondary" onClick={formatAlignLeft} title="Align Left">
                                <FaAlignLeft />
                            </Button>
                            <Button type="button" variant="outline-secondary" onClick={formatAlignCenter} title="Align Center">
                                <FaAlignCenter />
                            </Button>
                            <Button type="button" variant="outline-secondary" onClick={formatAlignRight} title="Align Right">
                                <FaAlignRight />
                            </Button>
                        </ButtonGroup>

                        <Dropdown className="d-inline-block me-2">
                            <Dropdown.Toggle type="button" variant="outline-secondary" size="sm">
                                <FaFont /> Size
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {fontSizes.map((size) => (
                                    <Dropdown.Item 
                                        key={size.value} 
                                        onClick={() => handleFontSize(size.value)}
                                    >
                                        {size.label}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>

                        <Dropdown className="d-inline-block me-2">
                            <Dropdown.Toggle type="button" variant="outline-secondary" size="sm">
                                <FaPalette /> Text Color
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <div className="color-grid">
                                    {colors.map((color) => (
                                        <div
                                            key={color}
                                            className="color-option"
                                            style={{ backgroundColor: color }}
                                            onClick={() => handleTextColor(color)}
                                            title={color}
                                        />
                                    ))}
                                </div>
                            </Dropdown.Menu>
                        </Dropdown>

                        <Dropdown className="d-inline-block me-2">
                            <Dropdown.Toggle type="button" variant="outline-secondary" size="sm">
                                <FaPalette /> Background
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <div className="color-grid">
                                    {colors.map((color) => (
                                        <div
                                            key={color}
                                            className="color-option"
                                            style={{ backgroundColor: color }}
                                            onClick={() => handleBackgroundColor(color)}
                                            title={color}
                                        />
                                    ))}
                                </div>
                            </Dropdown.Menu>
                        </Dropdown>

                        <ButtonGroup size="sm">
                            <Button type="button" variant="outline-secondary" onClick={formatUndo} title="Undo">
                                <FaUndo />
                            </Button>
                            <Button type="button" variant="outline-secondary" onClick={formatRedo} title="Redo">
                                <FaRedo />
                            </Button>
                        </ButtonGroup>
                    </>
                )}
            </div>

            {/* Editor Content */}
            <div
                ref={editorRef}
                className="editor-content"
                contentEditable
                onInput={handleInput}
                onBlur={saveSelection}
                onMouseUp={saveSelection}
                onKeyUp={saveSelection}
                data-placeholder={placeholder}
                suppressContentEditableWarning={true}
                style={{
                    minHeight: '150px',
                    padding: '12px',
                    border: '1px solid #ced4da',
                    borderRadius: '0 0 0.375rem 0.375rem',
                    outline: 'none',
                    backgroundColor: 'white'
                }}
            />
        </div>
    );
};

export default CustomRichTextEditor;
